import { useDialog } from 'contexts/dialog';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import assignmentService from 'services/assignment';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Loading,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    ResponsiveDialog,
    RubricTable,
    Select,
    Stack,
    styled,
    Tag,
    Text,
    TextInput,
    Tooltip,
    ViewMore,
} from 'shell/components';
import { useViewAccordionRubricTableButton } from 'shell/components/ViewRubricTable';
import { CITATION_SELECT_OPTIONS } from 'utils/constants';
import { hasCriterionInstructions } from 'utils/gradingInstructions';
import { stripHTML } from 'utils/helper';

const GROUPS = [
    { id: 'description', translationKey: 'rubric.form.description-default', label: 'Description' },
    { id: 'rubric', translationKey: 'rubric.title-rubric', label: 'Rubric' },
    { id: 'citationFormat', translationKey: 'citation-format.label', label: 'Citation Format' },
    { id: 'contentBank', translationKey: 'content-bank.title', label: 'Content Bank' },
    { id: 'grading', translationKey: 'grading-section.grading-settings', label: 'Grading Settings' },
    { id: 'feedback', translationKey: 'feedback-section.title', label: 'Feedback Settings' },
    { id: 'formative', translationKey: 'formative-feedback.settings-title', label: 'Formative Feedback Settings' },
    {
        id: 'inlineComments',
        translationKey: 'inline-comments-section.inline-comments-settings',
        label: 'Inline Comments Settings',
    },
];

const getCourseMeta = (course, t) => {
    const term =
        course.semester ||
        (course.created_on ? t('common:ui.format-date-short', '{{val, date_short}}', { val: course.created_on }) : '');
    const section = course.section
        ? t('assignment-rollover.course-section', 'Section {{section}}', { section: course.section })
        : '';
    return [term, section].filter(Boolean).join(' / ');
};

const normalizeCourses = (response) => {
    const courses = response?.courses || [];
    if (courses.every((course) => Array.isArray(course.assignments))) return courses;

    const assignments = response?.assignments || [];
    return courses.map((course) => ({
        ...course,
        assignments: assignments.filter(({ course_id: courseId }) => `${courseId}` === `${course.id}`),
    }));
};

const isTemplated = (settings) => settings?.structure === 'templated';

const getUnavailableReason = (groupId, source, disabledGroups, t) => {
    if (disabledGroups.includes(groupId))
        return t('assignment-rollover.setting-disabled', 'The administrator has disabled this setting.');
    if (groupId === 'feedback' && isTemplated(source?.ai_feedback_settings)) {
        return t(
            'assignment-rollover.templates-unsupported',
            'Smart Feedback Templates cannot be supported for rollover.'
        );
    }
    if (groupId === 'formative' && isTemplated(source?.ai_student_feedback_settings)) {
        return t(
            'assignment-rollover.templates-unsupported',
            'Smart Feedback Templates cannot be supported for rollover.'
        );
    }
    return '';
};

const getSettingStatus = (source, groupId) => {
    const persistedValue = {
        grading: source?.ai_grading_enabled,
        feedback: source?.ai_feedback_enabled,
        formative: source?.ai_student_feedback_enabled,
        inlineComments: source?.ai_inline_comments_enabled,
    }[groupId];
    const value =
        persistedValue ??
        {
            grading: true,
            feedback: true,
            formative: false,
            inlineComments: true,
        }[groupId];
    return value ? 'ON' : 'OFF';
};

const buildDefaultGradingMapping = ({ source, targetRubric }) => {
    const sourceCriteria = source?.rubric?.criteria || [];
    const targetCriteria = targetRubric?.criteria || [];
    const instructions = source?.ai_grading_settings?.criteria || {};
    const sourceCriterionIds = sourceCriteria.map(({ id }) => `${id}`);
    const sourceIds = [...sourceCriterionIds, ...Object.keys(instructions)].filter(
        (id, index, values) => values.indexOf(id) === index
    );

    return sourceIds.reduce((result, sourceId) => {
        const sourceIndex = sourceCriterionIds.indexOf(sourceId);
        const targetCriterionId = sourceIndex === -1 ? null : targetCriteria[sourceIndex]?.id;
        result[sourceId] = targetCriterionId === undefined || targetCriterionId === null ? '' : `${targetCriterionId}`;
        return result;
    }, {});
};

const ImportAssignmentSettingsDialog = memo(
    ({ currentAssignmentId, currentForm, disabledGroups = [], open, onClose, onImport }) => {
        const { t } = useTranslation('assignment');
        const { closeDialog, setDialog } = useDialog();
        const { AccordionRubricTableButton, isExpanded: isRubricExpanded } = useViewAccordionRubricTableButton();
        const [phase, setPhase] = useState('choose');
        const [loading, setLoading] = useState(false);
        const [courses, setCourses] = useState([]);
        const [courseLoadError, setCourseLoadError] = useState(false);
        const [sourceLoadError, setSourceLoadError] = useState('');
        const [search, setSearch] = useState('');
        const [expandedCourseId, setExpandedCourseId] = useState('');
        const [selectedAssignmentId, setSelectedAssignmentId] = useState('');
        const [generatedId, setGeneratedId] = useState('');
        const [generatedIdError, setGeneratedIdError] = useState('');
        const [sourceResult, setSourceResult] = useState(null);
        const [selectedGroups, setSelectedGroups] = useState([]);
        const [gradingMapping, setGradingMapping] = useState({});
        const [rubricReturnPhase, setRubricReturnPhase] = useState('settings');

        const source = sourceResult?.assignment;
        const sourceCourse = sourceResult?.course;
        const effectiveTargetRubric = selectedGroups.includes('rubric') ? source?.rubric : currentForm?.rubric;

        const unavailableByGroup = useMemo(
            () =>
                GROUPS.reduce((result, { id }) => {
                    result[id] = getUnavailableReason(id, source, disabledGroups, t);
                    return result;
                }, {}),
            [disabledGroups, source, t]
        );

        const availableGroups = useMemo(
            () => GROUPS.filter(({ id }) => !unavailableByGroup[id]).map(({ id }) => id),
            [unavailableByGroup]
        );

        const reset = useCallback(() => {
            setPhase('choose');
            setLoading(false);
            setCourses([]);
            setCourseLoadError(false);
            setSourceLoadError('');
            setSearch('');
            setExpandedCourseId('');
            setSelectedAssignmentId('');
            setGeneratedId('');
            setGeneratedIdError('');
            setSourceResult(null);
            setSelectedGroups([]);
            setGradingMapping({});
            setRubricReturnPhase('settings');
        }, []);

        useEffect(() => {
            if (open) reset();
        }, [open, reset]);

        useEffect(() => {
            if (!source) return;
            setSelectedGroups((current) => current.filter((id) => !unavailableByGroup[id]));
        }, [source, unavailableByGroup]);

        useEffect(() => {
            if (!source) return;
            setGradingMapping(buildDefaultGradingMapping({ source, targetRubric: effectiveTargetRubric }));
        }, [effectiveTargetRubric, source]);

        const close = () => {
            reset();
            onClose();
        };

        const requestClose = () => {
            if (['settings', 'rubric', 'grading', 'confirm-settings'].includes(phase)) {
                setDialog({
                    open: true,
                    message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                    description: t(
                        'common:dialog.unsaved-changes.description',
                        'If you exit without saving, all unsaved changes will be lost.'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.exit', 'Exit'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => {
                        closeDialog();
                        close();
                    },
                    onCancel: closeDialog,
                    onClose: closeDialog,
                });
                return;
            }
            close();
        };

        const openRubricPreview = () => {
            setRubricReturnPhase(phase);
            setPhase('rubric');
        };

        const openGradingPreview = () => {
            setRubricReturnPhase(phase);
            setPhase('grading');
        };

        const loadCourses = async () => {
            setPhase('courses');
            setLoading(true);
            setCourseLoadError(false);
            setSourceLoadError('');
            try {
                const response = await assignmentService.listRolloverSources();
                setCourses(normalizeCourses(response));
            } catch (error) {
                setCourses([]);
                setCourseLoadError(true);
            } finally {
                setLoading(false);
            }
        };

        const loadSource = async ({ assignmentId, assignmentGeneratedId }) => {
            setLoading(true);
            setGeneratedIdError('');
            setSourceLoadError('');
            try {
                const result = await assignmentService.getRolloverSource({
                    assignmentId,
                    generatedId: assignmentGeneratedId,
                });
                if (!result?.assignment || `${result.assignment.id}` === `${currentAssignmentId || ''}`) {
                    const error = new Error('invalid source');
                    error.isInvalidSource = true;
                    throw error;
                }
                setSourceResult(result);
                return result;
            } catch (error) {
                if (assignmentGeneratedId) {
                    setGeneratedIdError(
                        error?.status === 404 || error?.response?.status === 404 || error?.isInvalidSource
                            ? t('assignment-rollover.invalid-id', 'Enter a valid assignment ID')
                            : t(
                                  'assignment-rollover.lookup-error',
                                  'Unable to search for this assignment. Please try again.'
                              )
                    );
                } else {
                    setSourceLoadError(
                        t('assignment-rollover.source-load-error', 'Unable to load this assignment. Please try again.')
                    );
                }
                return null;
            } finally {
                setLoading(false);
            }
        };

        const handleCourseNext = async () => {
            if (!selectedAssignmentId) return;
            const result = await loadSource({ assignmentId: selectedAssignmentId });
            if (result) setPhase('settings');
        };

        const handleIdSearch = async () => {
            if (!generatedId.trim()) {
                setGeneratedIdError(t('assignment-rollover.invalid-id', 'Enter a valid assignment ID'));
                return;
            }
            const result = await loadSource({ assignmentGeneratedId: generatedId.trim() });
            if (result) setPhase('confirm-source');
        };

        const handleToggleGroup = (groupId) => {
            if (unavailableByGroup[groupId]) return;
            setSelectedGroups((current) =>
                current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId]
            );
        };

        const handleGradingMappingChange = (sourceId, targetId) => {
            setGradingMapping((current) => {
                const next = { ...current };
                if (targetId) {
                    Object.keys(next).forEach((otherSourceId) => {
                        if (otherSourceId !== sourceId && `${next[otherSourceId]}` === `${targetId}`) {
                            next[otherSourceId] = '';
                        }
                    });
                }
                next[sourceId] = targetId;
                return next;
            });
        };

        const handleSelectAll = () => {
            setSelectedGroups((current) => (current.length === availableGroups.length ? [] : [...availableGroups]));
        };

        const handleConfirmImport = () => {
            onImport({ source, selectedGroups, gradingMapping });
            close();
        };

        const filteredCourses = useMemo(() => {
            const value = search.trim().toLowerCase();
            return courses
                .map((course) => {
                    const assignments = (course.assignments || []).filter(
                        ({ id }) => `${id}` !== `${currentAssignmentId || ''}`
                    );
                    if (!value) return { ...course, assignments };
                    const courseMatches = `${course.name || ''}`.toLowerCase().includes(value);
                    const matchingAssignments = assignments.filter(({ title }) =>
                        `${title || ''}`.toLowerCase().includes(value)
                    );
                    return {
                        ...course,
                        assignments: courseMatches ? assignments : matchingAssignments,
                        forceExpanded: matchingAssignments.length > 0,
                    };
                })
                .filter((course) =>
                    value ? `${course.name || ''}`.toLowerCase().includes(value) || course.assignments.length : true
                );
        }, [courses, currentAssignmentId, search]);

        const renderHeader = (title, subtitle) => {
            const isLargeDialog = ['settings', 'rubric', 'grading', 'confirm-settings'].includes(phase);
            const isCentered = phase === 'choose';

            return (
                <>
                    <DialogTitle>
                        <HeaderStack
                            alignItems={isCentered ? 'center' : 'flex-start'}
                            direction={'row'}
                            justifyContent={'space-between'}
                            spacing={4}
                        >
                            <Stack
                                alignItems={isCentered ? 'center' : 'flex-start'}
                                sx={{ minWidth: 0, width: '100%' }}
                                spacing={2}
                            >
                                <Text
                                    align={isCentered ? 'center' : 'left'}
                                    id={'assignment-rollover-dialog-title'}
                                    component={'h1'}
                                    variant={isLargeDialog ? 'h6' : 'sh1'}
                                    weight={isLargeDialog ? 'medium' : 'semibold'}
                                >
                                    {title}
                                </Text>
                                {subtitle && (
                                    <Text align={isCentered ? 'center' : 'left'} color={'light'}>
                                        {subtitle}
                                    </Text>
                                )}
                            </Stack>
                            <HeaderCloseButton
                                aria-label={t('common:controls.close', 'Close')}
                                kind={'exit'}
                                size={'medium'}
                                sx={isCentered ? { position: 'absolute', top: 0, right: 0 } : undefined}
                                variant={'square'}
                                onClick={requestClose}
                            />
                        </HeaderStack>
                    </DialogTitle>
                    {isLargeDialog && <Divider />}
                </>
            );
        };

        const renderChoose = () => (
            <>
                {renderHeader(
                    t('assignment-rollover.choose-title', 'Import Assignment Settings'),
                    t('assignment-rollover.choose-description', 'Choose how to import your assignment settings:')
                )}
                <DialogContent>
                    <ModeGrid>
                        <ModeCard>
                            <Stack alignItems={'center'} spacing={3}>
                                <Text variant={'p1'} weight={'medium'}>
                                    {t('assignment-rollover.search-list-title', 'Search Through List')}
                                </Text>
                                <Text align={'center'} color={'light'}>
                                    {t(
                                        'assignment-rollover.search-list-description',
                                        'Search by course or assignment name, then choose the one you want.'
                                    )}
                                </Text>
                            </Stack>
                            <Button onClick={loadCourses}>{t('common:ui.select', 'Select')}</Button>
                        </ModeCard>
                        <ModeCard>
                            <Stack alignItems={'center'} spacing={3}>
                                <Text variant={'p1'} weight={'medium'}>
                                    {t('assignment-rollover.search-id-title', 'Search by ID')}
                                </Text>
                                <Text align={'center'} color={'light'}>
                                    {t(
                                        'assignment-rollover.search-id-description',
                                        "If the desired assignment isn't in your current courses, enter the assignment ID."
                                    )}
                                </Text>
                            </Stack>
                            <Button variant={'secondary'} onClick={() => setPhase('id')}>
                                {t('common:ui.select', 'Select')}
                            </Button>
                        </ModeCard>
                    </ModeGrid>
                </DialogContent>
            </>
        );

        const renderCourses = () => (
            <>
                {renderHeader(
                    t('assignment-rollover.find-title', 'Find Assignments Through Your Courses'),
                    t(
                        'assignment-rollover.find-description',
                        'Find assignments by searching your course or assignment name.'
                    )
                )}
                <DialogContent>
                    <Stack spacing={5}>
                        <TextInput
                            aria-label={t('assignment-rollover.search-placeholder', 'Search course or assignment name')}
                            endIcon={<Icon kind={'search'} />}
                            placeholder={t(
                                'assignment-rollover.search-placeholder',
                                'Search course or assignment name'
                            )}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                                setSelectedAssignmentId('');
                                setSourceLoadError('');
                            }}
                        />
                        <CourseResults variant={'outlined'}>
                            {loading ? (
                                <Loading sx={{ minHeight: '320px' }} />
                            ) : courseLoadError ? (
                                <CenteredBox>
                                    <Stack alignItems={'center'} spacing={3}>
                                        <Text align={'center'}>
                                            {t(
                                                'assignment-rollover.course-load-error',
                                                'Unable to load your courses. Please try again.'
                                            )}
                                        </Text>
                                        <Button variant={'secondary'} onClick={loadCourses}>
                                            {t('common:controls.try-again', 'Try Again')}
                                        </Button>
                                    </Stack>
                                </CenteredBox>
                            ) : filteredCourses.length ? (
                                <RadioGroup
                                    name={'assignment-rollover-source'}
                                    value={selectedAssignmentId}
                                    onChange={(event) => {
                                        setSelectedAssignmentId(event.target.value);
                                        setSourceLoadError('');
                                    }}
                                >
                                    {filteredCourses.map((course) => {
                                        const expanded = course.forceExpanded || expandedCourseId === `${course.id}`;
                                        return (
                                            <CourseAccordion
                                                key={course.id}
                                                expanded={expanded}
                                                onChange={(_, nextExpanded) =>
                                                    setExpandedCourseId(nextExpanded ? `${course.id}` : '')
                                                }
                                            >
                                                <CourseSummary>
                                                    <Stack spacing={2}>
                                                        <ClampedText weight={'medium'}>{course.name}</ClampedText>
                                                        <Text color={'light'}>{getCourseMeta(course, t)}</Text>
                                                    </Stack>
                                                </CourseSummary>
                                                <CourseDetails>
                                                    {(course.assignments || []).length ? (
                                                        <Stack spacing={1}>
                                                            {course.assignments.map((assignment) => {
                                                                const checked =
                                                                    `${assignment.id}` === `${selectedAssignmentId}`;
                                                                return (
                                                                    <AssignmentOption
                                                                        key={assignment.id}
                                                                        $selected={checked}
                                                                    >
                                                                        <Radio
                                                                            label={
                                                                                <ClampedText component={'span'}>
                                                                                    {assignment.title}
                                                                                </ClampedText>
                                                                            }
                                                                            value={`${assignment.id}`}
                                                                        />
                                                                    </AssignmentOption>
                                                                );
                                                            })}
                                                        </Stack>
                                                    ) : (
                                                        <Text align={'center'} color={'light'}>
                                                            {t(
                                                                'assignment-rollover.no-course-assignments',
                                                                'No assignments found in this course.'
                                                            )}
                                                        </Text>
                                                    )}
                                                </CourseDetails>
                                            </CourseAccordion>
                                        );
                                    })}
                                </RadioGroup>
                            ) : (
                                <CenteredBox>
                                    <Text align={'center'}>
                                        {t(
                                            'assignment-rollover.no-results',
                                            'Unable to find the course or assignment name you provided.'
                                        )}
                                    </Text>
                                </CenteredBox>
                            )}
                        </CourseResults>
                        {sourceLoadError && <Text color={'error'}>{sourceLoadError}</Text>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button disabled={!selectedAssignmentId} loading={loading} onClick={handleCourseNext}>
                        {t('common:controls.next', 'Next')}
                    </Button>
                </DialogActions>
            </>
        );

        const renderId = () => (
            <>
                {renderHeader(t('assignment-rollover.id-title', 'Search Assignment ID'))}
                <DialogContent>
                    <Stack spacing={8}>
                        <Stack alignItems={'center'} direction={'row'} spacing={3}>
                            <Text color={'light'}>
                                {t('assignment-rollover.id-help-label', 'How can I get the assignment ID?')}
                            </Text>
                            <Tooltip
                                content={t(
                                    'assignment-rollover.id-help',
                                    'Open the assignment in TimelyGrader, generate its Assignment ID, then copy it here.'
                                )}
                                type={'info'}
                            />
                        </Stack>
                        <TextInput
                            required
                            error={Boolean(generatedIdError)}
                            helperText={generatedIdError}
                            label={t('assignment-rollover.assignment-id', 'Assignment ID')}
                            placeholder={t('assignment-rollover.enter-id', 'Enter assignment ID')}
                            value={generatedId}
                            onChange={(event) => {
                                setGeneratedId(event.target.value);
                                setGeneratedIdError('');
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') handleIdSearch();
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button loading={loading} onClick={handleIdSearch}>
                        {t('common:controls.search', 'Search')}
                    </Button>
                </DialogActions>
            </>
        );

        const renderConfirmSource = () => (
            <>
                {renderHeader(
                    t('assignment-rollover.confirm-source-title', 'Confirm Assignment'),
                    t(
                        'assignment-rollover.confirm-source-description',
                        "Do you want to use this assignment's settings to replace the current settings?"
                    )
                )}
                <DialogContent>
                    <SourceCard variant={'outlined'}>
                        <Stack spacing={4}>
                            <Text variant={'p1'} weight={'medium'}>
                                {source?.title}
                            </Text>
                            <Divider />
                            <Text color={'light'}>{sourceCourse?.name}</Text>
                            <Text color={'light'}>{getCourseMeta(sourceCourse || {}, t)}</Text>
                            <Stack direction={'row'} spacing={2}>
                                <Text color={'light'}>{t('assignment-rollover.assignment-id', 'Assignment ID')}</Text>
                                <Text weight={'medium'}>{source?.generated_id || '--'}</Text>
                            </Stack>
                        </Stack>
                    </SourceCard>
                </DialogContent>
                <DialogActions>
                    <Button variant={'borderless'} onClick={() => setPhase('id')}>
                        {t('assignment-rollover.return-to-search', 'No, return to search')}
                    </Button>
                    <Button onClick={() => setPhase('settings')}>{t('common:controls.next', 'Next')}</Button>
                </DialogActions>
            </>
        );

        const renderGroupPreview = (groupId) => {
            if (groupId === 'description') {
                return (
                    <ViewMore
                        collapseLabel={t('common:controls.collapse', 'Collapse')}
                        expandLabel={t('common:controls.expand', 'Expand')}
                        maxLines={3}
                        TextComponentProps={{ color: 'light', rich: true, variant: 'p3' }}
                        variant={'primary'}
                    >
                        {source?.description || t('common:ui.none', 'None')}
                    </ViewMore>
                );
            }
            if (groupId === 'rubric') {
                return (
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <Text color={'light'}>{source?.rubric?.title || t('common:ui.none', 'None')}</Text>
                        {source?.rubric && (
                            <Button variant={'borderless'} onClick={openRubricPreview}>
                                {t('common:controls.view', 'View')}
                            </Button>
                        )}
                    </Stack>
                );
            }
            if (groupId === 'citationFormat') {
                const rawCitationFormat = source?.citation_format;
                const citationFormat =
                    typeof rawCitationFormat === 'string' && rawCitationFormat.trim()
                        ? rawCitationFormat.trim()
                        : CITATION_SELECT_OPTIONS[0].key;
                const citationFormatFallback =
                    CITATION_SELECT_OPTIONS.find(({ key }) => key === citationFormat)?.value || citationFormat;
                return (
                    <ValueTag
                        label={t(`citation-format.option.${citationFormat}`, citationFormatFallback)}
                        type={'general'}
                        value={'status'}
                    />
                );
            }
            if (groupId === 'contentBank') {
                const count =
                    source?.content_bank_resources?.filter((resource) => resource && resource.active !== false)
                        .length || 0;
                return (
                    <Text color={'light'}>
                        {count
                            ? t('assignment-rollover.files-added', '{{count}} files added', { count })
                            : t('common:ui.none', 'None')}
                    </Text>
                );
            }
            return (
                <SettingsPreview disabled={disabledGroups.includes(groupId)} source={source} groupId={groupId}>
                    {groupId === 'grading' && (
                        <GradingMappingSummary
                            currentRubric={effectiveTargetRubric}
                            mapping={gradingMapping}
                            source={source}
                            onView={openGradingPreview}
                        />
                    )}
                </SettingsPreview>
            );
        };

        const renderSettings = () => (
            <>
                {renderHeader(t('assignment-rollover.settings-title', 'Select Assignment Settings'))}
                <DialogContent>
                    <Stack spacing={4}>
                        <Stack spacing={1}>
                            <Text color={'light'} variant={'p3'}>
                                {t('assignment-rollover.selected-assignment', 'Selected Assignment')}
                            </Text>
                            <Text weight={'medium'}>{source?.title}</Text>
                            <Text color={'light'} variant={'p3'}>
                                {sourceCourse?.name}
                            </Text>
                        </Stack>
                        <Notice>
                            <Text color={'inherit'} variant={'p3'}>
                                <Text color={'inherit'} component={'span'} variant={'p3'} weight={'medium'}>
                                    {t('common:ui.please-note', 'Please note:')}
                                </Text>{' '}
                                {t(
                                    'assignment-rollover.overwrite-note-description',
                                    'Choosing these options will overwrite the existing assignment settings.'
                                )}
                            </Text>
                        </Notice>
                        <SelectAllRow>
                            <Checkbox
                                aria-label={t('assignment-rollover.select-all', 'Select All Settings')}
                                checked={availableGroups.length > 0 && selectedGroups.length === availableGroups.length}
                                indeterminate={
                                    selectedGroups.length > 0 && selectedGroups.length < availableGroups.length
                                }
                                label={t('assignment-rollover.select-all', 'Select All Settings')}
                                onChange={handleSelectAll}
                            />
                            <Text color={'light'}>
                                {t('assignment-rollover.selected-count', '{{selected}} of {{total}} selected', {
                                    selected: selectedGroups.length,
                                    total: availableGroups.length,
                                })}
                            </Text>
                        </SelectAllRow>
                        {GROUPS.map(({ id, label, translationKey }) => {
                            const unavailableReason = unavailableByGroup[id];
                            const checked = selectedGroups.includes(id);
                            return (
                                <SettingCard key={id}>
                                    <Stack alignItems={'flex-start'} direction={'row'} spacing={2}>
                                        <Checkbox
                                            aria-label={t(translationKey, label)}
                                            checked={checked}
                                            disabled={Boolean(unavailableReason)}
                                            onChange={() => handleToggleGroup(id)}
                                        />
                                        <Stack sx={{ minWidth: 0, width: '100%' }} spacing={2}>
                                            <Text weight={'medium'}>{t(translationKey, label)}</Text>
                                            {unavailableReason && <Text color={'error'}>{unavailableReason}</Text>}
                                            <Box sx={{ opacity: unavailableReason ? 0.45 : 1 }}>
                                                {renderGroupPreview(id)}
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </SettingCard>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button variant={'borderless'} onClick={requestClose}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button disabled={!selectedGroups.length} onClick={() => setPhase('confirm-settings')}>
                        {t('common:ui.select', 'Select')}
                    </Button>
                </DialogActions>
            </>
        );

        const renderRubric = () => (
            <>
                {renderHeader(t('assignment-rollover.settings-title', 'Select Assignment Settings'))}
                <DialogContent>
                    <Stack spacing={5}>
                        <Button
                            sx={{ alignSelf: 'flex-start' }}
                            startIcon={<Icon kind={'arrowLeft'} />}
                            variant={'borderless'}
                            onClick={() => setPhase(rubricReturnPhase)}
                        >
                            {t('common:controls.back', 'Back')}
                        </Button>
                        <Text variant={'p1'} weight={'medium'}>
                            {t('assignment-rubric-label', 'Rubric')}
                        </Text>
                        <Notice>
                            <Text color={'inherit'} variant={'p3'}>
                                {t(
                                    'assignment-rollover.read-only-note',
                                    "Please note: This is a read-only preview; information can't be edited here. To make changes, complete the import first."
                                )}
                            </Text>
                        </Notice>
                        <Text color={'light'}>{t('assignment-rollover.view-only', 'Rubric (For viewing only)')}</Text>
                        <Text variant={'sh2'} weight={'semibold'}>
                            {source?.rubric?.title}
                        </Text>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={3}>
                            <Text>{t('assignment-rubric-label', 'Assignment Rubric')}</Text>
                            {Boolean(source?.rubric?.criteria?.length) && <AccordionRubricTableButton />}
                        </Stack>
                        {source?.rubric?.criteria?.length ? (
                            <RubricTable type={'view'} isExpanded={isRubricExpanded} data={source.rubric.criteria} />
                        ) : (
                            <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                        )}
                    </Stack>
                </DialogContent>
            </>
        );

        const renderGrading = () => (
            <>
                {renderHeader(t('assignment-rollover.settings-title', 'Select Assignment Settings'))}
                <DialogContent>
                    <Stack spacing={4}>
                        <Button
                            sx={{ alignSelf: 'flex-start' }}
                            startIcon={<Icon kind={'arrowLeft'} />}
                            variant={'borderless'}
                            onClick={() => setPhase(rubricReturnPhase)}
                        >
                            {t('common:controls.back', 'Back')}
                        </Button>
                        <Text variant={'p1'} weight={'medium'}>
                            {t('assignment-rollover.grading-instructions-title', 'Grading Instructions')}
                        </Text>
                        <Stack spacing={2}>
                            <Text color={'light'}>
                                {t(
                                    'assignment-rollover.grading-rubric-reference',
                                    'Rubric (For viewing and reference for grading instructions)'
                                )}
                            </Text>
                            <Text variant={'sh2'} weight={'semibold'}>
                                {source?.rubric?.title}
                            </Text>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={3}>
                                <Text>{t('assignment-rubric-label', 'Assignment Rubric')}</Text>
                                {Boolean(source?.rubric?.criteria?.length) && <AccordionRubricTableButton />}
                            </Stack>
                            {source?.rubric?.criteria?.length ? (
                                <RubricTable
                                    type={'view'}
                                    isExpanded={isRubricExpanded}
                                    data={source.rubric.criteria}
                                />
                            ) : (
                                <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                            )}
                        </Stack>
                        <Notice>
                            <Text color={'inherit'} variant={'p3'}>
                                {t(
                                    'assignment-rollover.mapping-note',
                                    'Instructions were mapped automatically. Verify each one matches the correct criterion, and adjust any that do not.'
                                )}
                            </Text>
                        </Notice>
                        <InstructionPanel>
                            <Stack spacing={2}>
                                <Text weight={'medium'}>
                                    {t(
                                        'assignment-rollover.general-grading-instructions',
                                        'General Grading Instructions'
                                    )}
                                </Text>
                                <Text color={'light'}>
                                    {stripHTML(source?.ai_grading_context) || t('common:ui.none', 'None')}
                                </Text>
                            </Stack>
                        </InstructionPanel>
                        <GradingMapping
                            currentRubric={effectiveTargetRubric}
                            mapping={gradingMapping}
                            source={source}
                            onChange={handleGradingMappingChange}
                        />
                    </Stack>
                </DialogContent>
            </>
        );

        const renderConfirmSettings = () => {
            const included = GROUPS.filter(({ id }) => selectedGroups.includes(id));
            const notIncluded = GROUPS.filter(({ id }) => !selectedGroups.includes(id));
            return (
                <>
                    {renderHeader(
                        t('assignment-rollover.confirm-settings-title', 'Confirm Assignment Settings for Import')
                    )}
                    <DialogContent>
                        <Stack spacing={5}>
                            <Stack direction={'row'} spacing={2}>
                                <Text color={'light'}>{t('assignment-rollover.applying-to', 'Applying to')}</Text>
                                <Text weight={'medium'}>
                                    {currentForm?.title || t('assignment-rollover.untitled', 'Untitled assignment')}
                                </Text>
                            </Stack>
                            <Text>{t('assignment-rollover.importing', 'Importing')}</Text>
                            {included.map(({ id, label, translationKey }) => (
                                <SettingCard key={id}>
                                    <Stack spacing={2}>
                                        <Text weight={'medium'}>{t(translationKey, label)}</Text>
                                        {renderGroupPreview(id)}
                                    </Stack>
                                </SettingCard>
                            ))}
                            {notIncluded.length > 0 && (
                                <Stack spacing={3}>
                                    <Text color={'light'}>{t('assignment-rollover.not-included', 'Not Included')}</Text>
                                    <SettingCard>
                                        <Stack spacing={2}>
                                            {notIncluded.map(({ id, label, translationKey }) => (
                                                <Text key={id}>{t(translationKey, label)}</Text>
                                            ))}
                                        </Stack>
                                    </SettingCard>
                                </Stack>
                            )}
                        </Stack>
                    </DialogContent>
                    <Divider />
                    <DialogActions>
                        <Button variant={'borderless'} onClick={() => setPhase('settings')}>
                            {t('assignment-rollover.back-to-edit', 'Back to Edit')}
                        </Button>
                        <Button onClick={handleConfirmImport}>{t('common:controls.confirm', 'Confirm')}</Button>
                    </DialogActions>
                </>
            );
        };

        const content = {
            choose: renderChoose,
            courses: renderCourses,
            id: renderId,
            'confirm-source': renderConfirmSource,
            settings: renderSettings,
            rubric: renderRubric,
            grading: renderGrading,
            'confirm-settings': renderConfirmSettings,
        }[phase];

        return (
            <CustomDialog
                aria-labelledby={'assignment-rollover-dialog-title'}
                $phase={phase}
                maxWidth={false}
                open={open}
                scroll={'paper'}
                onClose={requestClose}
            >
                {content?.()}
            </CustomDialog>
        );
    }
);

const SettingsPreview = ({ children, disabled = false, groupId, source }) => {
    const { t } = useTranslation('assignment');
    const persistedStatus = getSettingStatus(source, groupId);
    const status = disabled ? 'OFF' : persistedStatus;
    const settings = {
        feedback: source?.ai_feedback_settings,
        formative: source?.ai_student_feedback_settings,
        inlineComments: source?.ai_inline_comments_settings,
    }[groupId];
    const context = {
        grading: source?.ai_grading_context,
        feedback: source?.ai_feedback_context,
        formative: source?.ai_student_feedback_context,
        inlineComments: source?.ai_inline_comments_context,
    }[groupId];
    const structureLabel = {
        rubric: t('assignment-rollover.structure.rubric', 'Rubric criteria'),
        system: t('assignment-rollover.structure.system', 'General feedback'),
        custom: t('assignment-rollover.structure.custom', 'Custom instructions'),
        templated: t('feedback-section.templated-feedback', 'Smart Feedback Templates'),
    }[settings?.structure];
    const statusLabel = {
        grading: t('grading-section.grading-suggestions', 'Grading Suggestions'),
        feedback: t('feedback-section.final-submission-feedback-suggestions', 'Final Submission Feedback Suggestions'),
        formative: t('formative-feedback.student-title', 'Formative Feedback for Students'),
        inlineComments: t('assignment-rollover.status-label.inlineComments', 'Automatically Generate Inline Comments?'),
    }[groupId];

    return (
        <Stack spacing={2}>
            {(persistedStatus === 'ON' || disabled) && (
                <>
                    {settings && (
                        <PreviewList>
                            {structureLabel && (
                                <Text color={'inherit'} component={'li'} variant={'p3'}>
                                    {t('assignment-rollover.preview.type', 'Type: {{value}}', {
                                        value: structureLabel,
                                    })}
                                </Text>
                            )}
                            {settings.num_suggestions && (
                                <Text color={'inherit'} component={'li'} variant={'p3'}>
                                    {t(
                                        'assignment-rollover.preview.num-suggestions',
                                        'Number of suggestions: {{value}}',
                                        { value: settings.num_suggestions }
                                    )}
                                </Text>
                            )}
                            {settings.suggestion_length && (
                                <Text color={'inherit'} component={'li'} variant={'p3'}>
                                    {t('assignment-rollover.preview.length', 'Length: {{value}}', {
                                        value: settings.suggestion_length,
                                    })}
                                </Text>
                            )}
                            {settings.max_comments_per_page && (
                                <Text color={'inherit'} component={'li'} variant={'p3'}>
                                    {t(
                                        'assignment-rollover.preview.max-comments',
                                        'Maximum comments per page: {{value}}',
                                        { value: settings.max_comments_per_page }
                                    )}
                                </Text>
                            )}
                            {settings.comment_length && (
                                <Text color={'inherit'} component={'li'} variant={'p3'}>
                                    {t('assignment-rollover.preview.length', 'Length: {{value}}', {
                                        value: settings.comment_length,
                                    })}
                                </Text>
                            )}
                            {settings.language_level && (
                                <Text color={'inherit'} component={'li'} variant={'p3'}>
                                    {t('assignment-rollover.preview.language-level', 'Language level: {{value}}', {
                                        value: t(
                                            `feedback-settings.language-level.${settings.language_level}-label`,
                                            settings.language_level
                                        ),
                                    })}
                                </Text>
                            )}
                            {settings.tone && (
                                <Text color={'inherit'} component={'li'} variant={'p3'}>
                                    {t('assignment-rollover.preview.tone', 'Tone: {{value}}', {
                                        value: t(`feedback-settings.tone.${settings.tone}-label`, settings.tone),
                                    })}
                                </Text>
                            )}
                        </PreviewList>
                    )}
                    {settings?.structure === 'custom' && settings.custom_instructions && (
                        <PreviewText color={'light'}>
                            {t('assignment-rollover.preview.custom-instructions', 'Custom instructions: {{value}}', {
                                value: stripHTML(settings.custom_instructions),
                            })}
                        </PreviewText>
                    )}
                    {children}
                    {groupId !== 'grading' && context && (
                        <InstructionPanel>
                            <PreviewText color={'light'}>{stripHTML(context)}</PreviewText>
                        </InstructionPanel>
                    )}
                </>
            )}
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <Text color={'light'} variant={'p3'}>
                    {statusLabel}
                </Text>
                <StatusTag
                    $enabled={status === 'ON'}
                    label={
                        status === 'ON'
                            ? t('assignment-rollover.status.on', 'ON')
                            : t('assignment-rollover.status.off', 'OFF')
                    }
                    type={'general'}
                    value={'status'}
                />
            </Stack>
        </Stack>
    );
};

const getGradingRows = (source) => {
    const sourceCriteria = source?.rubric?.criteria || [];
    const instructions = source?.ai_grading_settings?.criteria || {};
    return [...sourceCriteria.map(({ id }) => `${id}`), ...Object.keys(instructions)].filter(
        (id, index, values) => values.indexOf(id) === index && hasCriterionInstructions(instructions[id])
    );
};

const CriterionInstructionPreview = ({ instruction }) => {
    const { t } = useTranslation('videoGrading');
    return (
        <Stack spacing={2}>
            {instruction?.grading_instructions && <Text rich>{instruction.grading_instructions}</Text>}
            {Boolean(instruction?.look_fors?.length) && (
                <Stack spacing={1}>
                    <Text color={'light'} variant={'p3'} weight={'medium'}>
                        {t('criterion.look_fors.preview-title', 'Look-fors')}
                    </Text>
                    {instruction.look_fors.map((entry, index) => (
                        <Text key={`${entry}-${index}`} color={'light'} variant={'p3'}>
                            • {entry}
                        </Text>
                    ))}
                </Stack>
            )}
            {Boolean(instruction?.violations_omissions?.length) && (
                <Stack spacing={1}>
                    <Text color={'light'} variant={'p3'} weight={'medium'}>
                        {t('criterion.violations_omissions.preview-title', 'Violations or omissions')}
                    </Text>
                    {instruction.violations_omissions.map((entry, index) => (
                        <Text key={`${entry}-${index}`} color={'light'} variant={'p3'}>
                            • {entry}
                        </Text>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

const GradingMappingSummary = ({ currentRubric, mapping, onView, source }) => {
    const { t } = useTranslation('assignment');
    const sourceCriteria = source?.rubric?.criteria || [];
    const targetCriteria = currentRubric?.criteria || [];
    const instructions = source?.ai_grading_settings?.criteria || {};
    const rows = getGradingRows(source);

    if (!rows.length) return null;

    return (
        <Stack spacing={2}>
            <Stack alignItems={'center'} direction={'row'} flexWrap={'wrap'} spacing={2}>
                <Text color={'error'} variant={'p3'}>
                    {t(
                        'assignment-rollover.verify-grading-mapping',
                        '! Verify that the grading instructions match the current rubric criteria.'
                    )}
                </Text>
                <Button variant={'borderless'} onClick={onView}>
                    {t('common:controls.view', 'View')}
                </Button>
            </Stack>
            {source?.ai_grading_context && (
                <InstructionPanel>
                    <Stack spacing={2}>
                        <Text weight={'medium'}>
                            {t('assignment-rollover.general-grading-instructions', 'General Grading Instructions')}
                        </Text>
                        <PreviewText color={'light'} variant={'p3'}>
                            {stripHTML(source.ai_grading_context)}
                        </PreviewText>
                    </Stack>
                </InstructionPanel>
            )}
            {rows.map((sourceId) => {
                const sourceCriterion = sourceCriteria.find(({ id }) => `${id}` === sourceId);
                const targetCriterion = targetCriteria.find(({ id }) => `${id}` === `${mapping[sourceId]}`);
                return (
                    <InstructionPanel key={sourceId}>
                        <Stack spacing={2}>
                            <Text color={'light'} variant={'p3'}>
                                {t(
                                    'assignment-rollover.criterion-grading-instructions',
                                    'Criterion Grading Instructions'
                                )}
                            </Text>
                            <Text weight={'medium'}>{sourceCriterion?.title || sourceId}</Text>
                            <CriterionInstructionPreview instruction={instructions[sourceId]} />
                            <Text color={'light'} variant={'p3'}>
                                {t('assignment-rollover.remapping-criterion', 'Remapping Criterion')}
                            </Text>
                            <Stack alignItems={'center'} direction={'row'} flexWrap={'wrap'} spacing={3}>
                                <Text variant={'p3'} weight={'medium'}>
                                    {sourceCriterion?.title || sourceId}
                                </Text>
                                <Icon kind={'arrowRight'} />
                                <Text variant={'p3'} weight={'medium'}>
                                    {targetCriterion?.title || '--'}
                                </Text>
                            </Stack>
                        </Stack>
                    </InstructionPanel>
                );
            })}
        </Stack>
    );
};

const GradingMapping = ({ currentRubric, mapping, onChange, source }) => {
    const { t } = useTranslation('assignment');
    const sourceCriteria = source?.rubric?.criteria || [];
    const targetCriteria = currentRubric?.criteria || [];
    const instructions = source?.ai_grading_settings?.criteria || {};
    const rows = getGradingRows(source);

    if (!rows.length) return null;

    return (
        <Stack spacing={3}>
            {!targetCriteria.length && (
                <Text color={'error'}>
                    {t(
                        'assignment-rollover.rubric-required',
                        'Select Rubric too, or add a rubric before importing criterion-specific grading instructions.'
                    )}
                </Text>
            )}
            {rows.map((sourceId) => {
                const sourceCriterion = sourceCriteria.find(({ id }) => `${id}` === sourceId);
                const labelId = `grading-mapping-${sourceId}`;
                return (
                    <MappingCard key={sourceId} variant={'outlined'}>
                        <Stack spacing={5}>
                            <Text weight={'medium'}>
                                {t(
                                    'assignment-rollover.criterion-specific-instructions',
                                    'Criterion-Specific Instructions'
                                )}
                            </Text>
                            <Text color={'light'}>
                                <Text color={'inherit'} component={'span'} weight={'medium'}>
                                    {t('assignment-rollover.from', 'From:')}{' '}
                                </Text>
                                {sourceCriterion?.title || sourceId}
                            </Text>
                            <MappingSelect
                                aria-label={t('assignment-rollover.maps-to-label', 'Maps to current criterion')}
                                disabled={!targetCriteria.length}
                                label={t('assignment-rollover.maps-to', 'Maps to (Current Criterion)')}
                                labelId={labelId}
                                value={mapping[sourceId] || ''}
                                onChange={(event) => onChange(sourceId, event.target.value)}
                            >
                                <MenuItem value={''}>
                                    {t('assignment-rollover.choose-criterion', 'Choose criterion')}
                                </MenuItem>
                                {targetCriteria.map((criterion) => (
                                    <MenuItem key={criterion.id} value={`${criterion.id}`}>
                                        {criterion.title}
                                    </MenuItem>
                                ))}
                            </MappingSelect>
                            <Stack spacing={3}>
                                <Text color={'light'} weight={'medium'}>
                                    {t('assignment-rollover.grading-instructions-title', 'Grading Instructions')}
                                </Text>
                                <CriterionInstructionPreview instruction={instructions[sourceId]} />
                            </Stack>
                        </Stack>
                    </MappingCard>
                );
            })}
        </Stack>
    );
};

const CustomDialog = styled(ResponsiveDialog, {
    shouldForwardProp: (prop) => prop !== '$phase',
})(({ $phase, theme }) => {
    const isLargeDialog = ['settings', 'rubric', 'grading', 'confirm-settings'].includes($phase);
    const width = isLargeDialog ? '1019px' : $phase === 'courses' ? '700px' : '600px';

    return {
        '& .MuiDialog-paper': {
            width,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            borderRadius: '16px',
        },
        '& .MuiDialogTitle-root': {
            padding: isLargeDialog
                ? theme.spacing(3, 5)
                : $phase === 'choose'
                  ? theme.spacing(8, 7, 0)
                  : theme.spacing(7, 7, 0),
            ...(isLargeDialog && {
                position: 'relative',
                zIndex: 1,
                boxShadow: theme.palette.ensightful.shadows[1],
            }),
        },
        '& .MuiDialogContent-root': {
            padding: isLargeDialog ? theme.spacing(5) : theme.spacing(5, 7),
            ...($phase === 'choose' && { paddingBottom: theme.spacing(8) }),
        },
        '& .MuiDialogActions-root': {
            gap: theme.spacing(3),
            justifyContent: 'flex-end',
            padding: isLargeDialog ? theme.spacing(3, 5) : theme.spacing(3, 7, 7),
        },
    };
});

const HeaderStack = styled(Stack)(() => ({
    position: 'relative',
    width: '100%',
}));

const HeaderCloseButton = styled(IconButton)(() => ({
    flexShrink: 0,
}));

const ModeGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(5),
    [theme.breakpoints.down('tablet')]: {
        gridTemplateColumns: '1fr',
    },
}));

const ModeCard = styled(Paper)(({ theme }) => ({
    display: 'flex',
    minHeight: '184px',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(5),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
    padding: theme.spacing(5),
    boxShadow: theme.palette.ensightful.shadows[2],
}));

const CourseResults = styled(Paper)(({ theme }) => ({
    height: '600px',
    maxHeight: '55vh',
    overflowY: 'auto',
    padding: '0 !important',
    borderRadius: '16px',
    borderColor: theme.palette.ensightful.neutral[300],
}));

const CenteredBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    minHeight: '320px',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
}));

const CourseAccordion = styled(Accordion)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    '&:last-of-type': { borderBottom: 0 },
}));

const CourseSummary = styled(AccordionSummary)(({ theme }) => ({
    padding: theme.spacing(5),
    '& > .MuiAccordionSummary-content': { minWidth: 0 },
}));

const CourseDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const AssignmentOption = styled(Box)(({ $selected, theme }) => ({
    display: 'flex',
    width: '100%',
    minHeight: '64px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    border: $selected ? `1px solid ${theme.palette.ensightful.primary[500]}` : '1px solid transparent',
    borderRadius: '16px',
    padding: theme.spacing(3),
    textAlign: 'left',
    background: $selected ? theme.palette.ensightful.primary[50] : 'transparent',
    '& .MuiFormControlLabel-root': {
        width: '100%',
    },
}));

const ClampedText = styled(Text)(() => ({
    display: '-webkit-box',
    minWidth: 0,
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
}));

const SourceCard = styled(Paper)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(5),
}));

const Notice = styled(Box)(({ theme }) => ({
    alignSelf: 'flex-start',
    borderRadius: '16px',
    padding: theme.spacing(2, 3),
    color: theme.palette.ensightful.warning[900],
    background: theme.palette.ensightful.warning[100],
}));

const SelectAllRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    borderRadius: '12px',
    padding: theme.spacing(2, 4),
    background: theme.palette.ensightful.neutral[50],
}));

const SettingCard = styled(Paper)(({ theme }) => ({
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
    padding: theme.spacing(4),
    background: theme.palette.ensightful.shades[0],
}));

const PreviewText = styled(Text)(() => ({
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
}));

const ValueTag = styled(Tag)(({ theme }) => ({
    alignSelf: 'flex-start',
    minWidth: '180px',
    maxWidth: '100%',
    border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
    borderRadius: '16px',
    padding: theme.spacing(2, 3),
    background: theme.palette.ensightful.shades[0],
}));

const StatusTag = styled(Tag, {
    shouldForwardProp: (prop) => prop !== '$enabled',
})(({ $enabled, theme }) => ({
    flexShrink: 0,
    borderRadius: '10px',
    padding: theme.spacing(0.5, 2),
    color: $enabled ? theme.palette.ensightful.success[900] : theme.palette.ensightful.error[800],
    background: $enabled ? theme.palette.ensightful.success[100] : theme.palette.ensightful.error[100],
}));

const PreviewList = styled('ul')(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(5),
    color: theme.palette.ensightful.neutral[700],
}));

const InstructionPanel = styled(Box)(({ theme }) => ({
    borderRadius: '16px',
    padding: theme.spacing(4),
    background: theme.palette.ensightful.neutral[50],
}));

const MappingCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    background: theme.palette.ensightful.neutral[50],
}));

const MappingSelect = styled(Select)(() => ({
    width: 'min(500px, 100%)',
}));

export { GROUPS as ASSIGNMENT_ROLLOVER_GROUPS };
export default ImportAssignmentSettingsDialog;
